import { SUPPORT_URL } from '../../config';
import { Info } from '../design-system';

/** Visual of the control at its render site. `pill` carries the "Help"
 *  label next to the icon; `icon` drops the label for the My Packs header
 *  on mobile, where the row already holds Home + tabs + Log out. */
export type HelpLinkVariant = 'pill' | 'icon';

// Glass-pill look of `Button variant="glass"`, reproduced here instead of
// reused: this control is an anchor (it opens an external site in a new
// tab) and `Button` only renders a <button>. Its own `--co-HelpLink-*`
// namespace means an operator themes the help control without dragging
// every glass button along with it. Values mirror the variant's literals,
// so the two read as one family out of the box.
const FILL = 'var(--co-HelpLink-fill, rgba(255,255,255,0.1))';
const BORDER = 'var(--co-HelpLink-border, rgba(255,255,255,0.25))';
const TEXT = 'var(--co-HelpLink-text, #FFFFFF)';
const FONT_FAMILY = 'var(--co-HelpLink-fontFamily, Inter), system-ui, sans-serif';
const FONT_SIZE = 'var(--co-HelpLink-fontSize, 15px)';

// THE visibility switch. Default `none` — the help site is an operator
// decision (not every partner runs one), so the control ships hidden and
// only appears once the operator flips this knob in the Design Constructor.
const DISPLAY = 'var(--co-HelpLink-display, none)';

const BASE =
  'items-center justify-center gap-1.5 rounded-full select-none cursor-pointer font-semibold ' +
  'backdrop-blur ' +
  'transition-[filter] duration-150 ease-out hover:[filter:brightness(0.92)] ' +
  'focus-visible:outline-none focus-visible:[outline:2px_solid_var(--ui-accent)] ' +
  'focus-visible:[outline-offset:2px]';

/**
 * "Help" link to the partner's support / FAQ site, opened in a new tab
 * (same treatment as the operator footer links).
 *
 * Rendered on the pack page (under the RIP rail on desktop, below Potential
 * Hits on mobile) and in the My Packs header next to Log out — in both
 * templates. Every copy is hidden until the operator turns on
 * `HelpLink.display`, so one knob governs the whole set.
 */
export interface HelpLinkProps {
  variant?: HelpLinkVariant;
  /** Positioning / spacing from the call site. Visibility is NOT set here —
   *  a wrapper's breakpoint class decides *where* the control may show, the
   *  operator knob decides *whether* it does. */
  className?: string;
}

export function HelpLink({ variant = 'pill', className = '' }: HelpLinkProps) {
  const isIcon = variant === 'icon';

  return (
    <a
      data-co-component="HelpLink"
      href={SUPPORT_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Help"
      className={
        BASE +
        ' ' +
        (isIcon ? 'h-9 w-9' : 'h-9 px-3 @[600px]:h-11 @[600px]:px-4 @[600px]:gap-2') +
        (className ? ` ${className}` : '')
      }
      style={{
        display: DISPLAY,
        background: FILL,
        border: `1px solid ${BORDER}`,
        color: TEXT,
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZE,
      }}
    >
      {!isIcon && <span>Help</span>}
      <Info size={isIcon ? 20 : 18} aria-hidden className="shrink-0" />
    </a>
  );
}
